import api from "@/lib/axiosConfig";
import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export type NoteSortField = "updatedAt" | "createdAt" | "title";
export type NoteSortOrder = "asc" | "desc";

export interface Note {
  id: string;
  instanceId: string;
  title: string;
  content: string;
  updatedAt: string | null;
  createdAt: string | null;
  updatedById: string | null;
  createdById: string | null;
}

export interface ListNotesParams {
  search?: string;
  sortBy?: NoteSortField;
  sortOrder?: NoteSortOrder;
}

const baseRoute = "back/api/notes";

async function getNotes(params: ListNotesParams = {}): Promise<Note[]> {
  const searchParams = new URLSearchParams();
  if (params.search != null && params.search !== "")
    searchParams.set("search", params.search);
  if (params.sortBy) searchParams.set("sortBy", params.sortBy);
  if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
  const qs = searchParams.toString();
  const { data } = await api.get<Note[]>(`${baseRoute}${qs ? `?${qs}` : ""}`);
  return data;
}

export function useGetNotes(params: ListNotesParams = {}) {
  return useQuery({
    queryKey: ["notes", params],
    queryFn: () => getNotes(params),
    placeholderData: keepPreviousData,
  });
}

export async function getNote(id: string): Promise<Note> {
  const { data } = await api.get<Note>(`${baseRoute}/${id}`);
  return data;
}

export function useGetNote(id: string | undefined) {
  return useQuery({
    queryKey: ["note", id],
    queryFn: () => (id ? getNote(id) : undefined),
    enabled: !!id,
  });
}

type CreateNoteParams = { title: string; content?: string };
type CreateNoteResponse = Note;

async function createNote(params: CreateNoteParams): Promise<CreateNoteResponse> {
  const { data } = await api.post<CreateNoteResponse>(baseRoute, {
    title: params.title,
    content: params.content ?? "",
  });
  return data;
}

export function useCreateNote(
  options?: UseMutationOptions<CreateNoteResponse, Error, CreateNoteParams>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: createNote,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["notes"] });
      queryClient.invalidateQueries({ queryKey: ["note", data.id] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
}

type UpdateNoteParams = { id: string; title?: string; content?: string };

async function updateNote(params: UpdateNoteParams): Promise<Note> {
  const { id, ...body } = params;
  const { data } = await api.patch<Note>(`${baseRoute}/${id}`, body);
  return data;
}

export function useUpdateNote(
  options?: UseMutationOptions<Note, Error, UpdateNoteParams>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: updateNote,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["notes"] });
      queryClient.invalidateQueries({ queryKey: ["note", data.id] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
}

async function deleteNote(id: string): Promise<void> {
  await api.delete(`${baseRoute}/${id}`);
}

export function useDeleteNote(
  options?: UseMutationOptions<void, Error, string>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: deleteNote,
    onSuccess: (_, id, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["notes"] });
      queryClient.removeQueries({ queryKey: ["note", id] });
      options?.onSuccess?.(_, id, ctx);
    },
  });
}
